import type { stacks } from "@dropbox/api-v2-client";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Initiate_UpdateStackEmoji } from "@mirage/analytics/events/types/initiate_update_stack_emoji";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { getDefaultColorIndex } from "@mirage/stacks/themes";
import {
  getStackIconUrl,
  getStackPapPropsWithFeatureLine,
} from "@mirage/stacks/utils";
import { EMOJI_CLEARED_SENTINEL_VALUE } from "@mirage/stacks/utils/helpers";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useUpdateStackEmoji, useUpdateStackLogo } from "../hooks";
import type { EmojiData } from "./EmojiPicker";
import styles from "./StackIconEditor.module.css";
import { StackIconPopover } from "./StackIconPopover";

export const StackIconEditor = ({
  canEdit,
  className,
  mutationRequestId,
  sessionId,
  stack,
}: {
  canEdit: boolean;
  className?: string;
  mutationRequestId?: string;
  sessionId?: string;
  stack: stacks.Stack | null;
}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const [popoverIsVisible, setIsPopoverVisible] = useState(false);
  const updateStackEmoji = useUpdateStackEmoji(
    stack,
    mutationRequestId,
    sessionId,
  );
  const isNewStack = stack === null;
  const colorIndex = isNewStack
    ? getDefaultColorIndex()
    : stack?.stack_data?.color_index || getDefaultColorIndex();
  const customColor = stack?.stack_data?.custom_color;
  const emoji = stack?.stack_data?.emoji;

  const stackIconContainerRef = useRef<HTMLDivElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const updateStackLogo = useUpdateStackLogo({
    isUploading,
    setIsUploading,
    stack,
  });

  const handleIconClick = () => {
    if (!canEdit || popoverIsVisible) return;
    setIsPopoverVisible(true);
    const papProps = getStackPapPropsWithFeatureLine(stack);
    reportPapEvent(PAP_Initiate_UpdateStackEmoji(papProps));
  };

  const handleEmojiSelect = (emoji: EmojiData) => {
    updateStackEmoji(emoji.native);
    setIsPopoverVisible(false);
  };

  return (
    <div
      className={classNames(styles.stackIconContainer, className)}
      data-testid="stackIcon"
      ref={stackIconContainerRef}
    >
      <DashStackIcon
        className={styles.stackIcon}
        colorIndex={colorIndex}
        aria-label={i18n.t("aria_emoji_button")}
        emoji={emoji || EMOJI_CLEARED_SENTINEL_VALUE}
        size="xlarge"
        onClick={handleIconClick}
        customColor={customColor}
        iconUrl={!isUploading ? getStackIconUrl(stack) : undefined}
        icon={isUploading ? <Spinner size="small" /> : undefined}
      />
      {canEdit && stack && (
        <StackIconPopover
          stack={stack}
          isOpen={popoverIsVisible}
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setIsPopoverVisible(false)}
          anchorRef={stackIconContainerRef}
          updateStackLogo={updateStackLogo}
          isUploading={isUploading}
        />
      )}
    </div>
  );
};
